import type { CentreData } from './types';
import { naplanTestPackLabels, naplanTestPackGradesByCourse } from '@/config/naplanTestPacks';

export const carolineSpringsData: CentreData = {
  slug: 'caroline-springs',
  name: 'Schoolola Caroline Springs',
  suburb: 'Caroline Springs',
  region: 'Western Melbourne',
  state: 'VIC',
  stateName: 'Victoria',
  status: 'operational',
  yearLevels: 'Prep - Year 8',
  canonicalPath: '/centres/caroline-springs/',
  heroImage: 'https://schoolola.com.au/centres/caroline-springs-og.jpg',
  address: {
    street: 'Level 1 B2, 9 Commercial Road',
    city: 'Caroline Springs',
    postcode: '3023',
    lat: -37.7398,
    lng: 144.7500,
  },
  contact: {
    phone: '03 7050 1666',
    phoneHref: '+61370501666',
    email: 'carolinesprings@schoololatutoring.com.au',
  },
  hours: {
    monday: { opens: '15:30', closes: '20:00' },
    tuesday: { opens: '15:30', closes: '20:00' },
    wednesday: { opens: '15:30', closes: '20:00' },
    thursday: { opens: '15:30', closes: '20:00' },
    friday: { opens: '15:30', closes: '20:00' },
    saturday: { opens: '09:00', closes: '13:00' },
    sunday: null,
  },
  heroSubtitle:
    'NAPLAN, selective entry and scholarship preparation by qualified Victorian Curriculum teachers.',
  trustStrip: [
    { k: '4.9 ★', v: 'Google rating, Caroline Springs' },
    { k: 'Prep - Year 8', v: 'All year levels supported' },
    { k: 'Victorian Curriculum', v: 'Specialist programs' },
  ],
  breadcrumb: { stateHref: '/states/victoria', regionLabel: 'Western Melbourne' },
  quickBook: {
    featuredCourses: [
      'Selective Entry High School Preparation',
      'SEC Exam Preparation',
      'Foundations Course',
      ...naplanTestPackLabels,
    ],
    eligibleGrades: ['Prep', 'Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5', 'Year 6', 'Year 7', 'Year 8'],
    gradesByCourse: {
      ...naplanTestPackGradesByCourse,
      'Selective Entry High School Preparation': ['Year 6', 'Year 7', 'Year 8'],
      'SEC Exam Preparation': ['Year 4', 'Year 5', 'Year 6'],
      'Foundations Course': ['Prep', 'Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5', 'Year 6', 'Year 7', 'Year 8'],
    },
    defaultMeetingType: 'in-person',
    allowOnline: true,
    eyebrow: 'Quick Book',
    title: 'Book your free assessment in 60 seconds',
    subtitle: 'A few quick taps and we\u2019ll lock in a real time with our Caroline Springs team.',
  },
  pathwayIntro:
    'Year 9 entry via the SEAL test taken in Year 8. Best results come from starting preparation in Year 5-6 and intensifying in Year 7-8.',
  googleRating: {
    value: 4.9,
    count: 12,
    profileUrl: 'https://www.google.com/search?q=Schoolola+Caroline+Springs',
  },
  reviews: [
    {
      author: 'Tuan Kha Pham Le',
      rating: 5,
      timeAgo: 'a month ago',
      datePublished: '2026-04-26',
      body: 'My son just started on selective class and after only one day he already loves it. He really enjoys the classes and said he can learn lots of new things, new skills and strategy to finish assessment.',
    },
    {
      author: 'Satheesh Kumar',
      rating: 5,
      timeAgo: '2 months ago',
      datePublished: '2026-03-26',
      isLocalGuide: true,
      body: 'The Schoolola tutoring has been great. We can definitely see her education improving, and the tuition support sessions have been really helpful. They also offer some fun extracurricular activities on Saturdays.',
    },
    {
      author: 'Angela Ly',
      rating: 5,
      timeAgo: '2 months ago',
      datePublished: '2026-03-26',
      body: 'Positive experience with this tutoring center. The space is clean, well-organized, and provides a comfortable environment for kids to focus and learn. The teachers are attentive and patient.',
    },
    {
      author: 'Preeti Bhawna',
      rating: 4,
      timeAgo: '2 months ago',
      datePublished: '2026-03-26',
      body: 'They are very organised, teachers teach well and on a high standard and pay attention to all students. Staff is very friendly and my child had improved a lot with Schoolola.',
    },
    {
      author: 'Kenny Nguyen',
      rating: 5,
      timeAgo: '2 months ago',
      datePublished: '2026-03-26',
      isLocalGuide: true,
      body: "This is a lovely place with plenty of natural light creating a great studying environment. There's even a small game area for students to relax after each class.",
    },
    {
      author: 'Hung Nguyen',
      rating: 5,
      timeAgo: '2 months ago',
      datePublished: '2026-03-26',
      body: 'My daughter just started attending, and after only one day she already loves it. She really enjoys the classes, activities with the teachers, and spending time with her new friends.',
    },
  ],
  suburbsServed: ['Caroline Springs'],
  localSchools: [
    { name: 'Caroline Springs College', yearLevels: 'P-9', type: 'Public', distanceKm: 2.1 },
    { name: 'Springside West Secondary', yearLevels: '7-12', type: 'Public', distanceKm: 1.4 },
    { name: 'Mowbray College', yearLevels: 'P-12', type: 'Independent', distanceKm: 3.7 },
    { name: 'Christ the Priest Catholic', yearLevels: 'Prep-6', type: 'Catholic', distanceKm: 1.9 },
  ],
  selectiveSchools: [
    { name: 'Suzanne Cory High', location: 'Werribee', distanceKm: 16, status: { label: 'Closest selective', tone: 'info' } },
    { name: 'Mac.Robertson Girls', location: 'Melbourne', distanceKm: 24, status: { label: 'Very competitive', tone: 'warning' } },
    { name: 'Melbourne High', location: 'South Yarra', distanceKm: 28, notes: 'Boys', status: { label: 'Very competitive', tone: 'warning' } },
    { name: 'Nossal High', location: 'Berwick', distanceKm: 56, status: { label: 'Moderate demand', tone: 'info' } },
  ],
  programs: [
    {
      id: 'foundations',
      title: 'Foundations Course',
      yearPill: { label: 'Prep - Year 8', tone: 'success' },
      tagline: 'Core English and maths · all year levels',
      bullets: ['Phonics, fluency, comprehension', 'Number sense to algebra readiness', 'Writing structure across genres'],
      educationalLevel: 'Prep to Year 8',
      description: 'Core English and mathematics tutoring for Prep through Year 8 students at Schoolola Caroline Springs, aligned to the Victorian Curriculum.',
    },
    {
      id: 'naplan',
      title: 'NAPLAN Preparation',
      yearPill: { label: 'Yr 3, 5', tone: 'info' },
      tagline: '10-week term aligned to ACARA',
      bullets: ['Reading and writing', 'Numeracy strategies', 'Language conventions'],
      educationalLevel: 'Year 3 and Year 5',
      description: '10-week term NAPLAN preparation course covering reading, writing, language conventions and numeracy, aligned to ACARA test specifications.',
    },
    {
      id: 'selective',
      title: 'Selective Entry High School Preparation',
      yearPill: { label: 'Most popular', tone: 'info' },
      tagline: 'Year 5-8 · Selective entry track',
      bullets: ['Verbal and quantitative reasoning', 'Reading comprehension', 'Mathematical reasoning'],
      featured: 'popular',
      educationalLevel: 'Year 4 to Year 6',
      description: 'Selective entry high school preparation covering verbal reasoning, quantitative reasoning, reading comprehension and mathematical reasoning for the Victorian SEAL test pathway.',
    },
    {
      id: 'sec',
      title: 'SEC Scholarship Exam Course',
      yearPill: { label: 'Yr 4-6', tone: 'info' },
      tagline: 'ACER and Edutest scholarship preparation',
      bullets: ['SEAL/SEALP training', 'Strategic exam techniques', 'Practice test cycles'],
      educationalLevel: 'Year 4 to Year 6',
      description: 'Schoolola SEC programmes - SEC TMRW (booklet-based) and the SEC Scholarship Exam Course - preparing students primarily for ACER and Edutest scholarship exams, with SEAL/SEALP training and structured practice test cycles.',
      prerequisites: 'Basic literacy and numeracy at year-level expectations.',
    },
    {
      id: 'assessment',
      title: 'General Assessment',
      yearPill: { label: 'All ages', tone: 'info' },
      tagline: 'Diagnostic assessment for new students',
      bullets: ['Reading, writing, maths diagnostic', '1:1 parent consultation', 'Personalised learning plan'],
      educationalLevel: 'All year levels',
      description: 'Free diagnostic assessment covering reading, writing and maths, followed by a 1:1 parent consultation and a written personalised learning plan.',
    },
  ],
  differentiators: [
    {
      title: 'Victorian Curriculum specialists',
      body: "Our team has analysed the curriculum in depth. Lessons map to what your child's school actually teaches, not generic worksheets.",
      icon: 'BookOpen',
    },
    {
      title: 'Measurable progress reports',
      body: 'Quarterly assessments tracked against ACARA bands. Parents see exactly where their child sits.',
      icon: 'LineChart',
    },
    {
      title: 'Small group ratios',
      body: 'Maximum 6 students per tutor. Enough peer dynamics, enough individual attention.',
      icon: 'Users',
    },
    {
      title: 'Selective and scholarship track record',
      body: 'Network students placed at Suzanne Cory, Mac.Robertson Girls, Melbourne High and Nossal High.',
      icon: 'Trophy',
    },
  ],
  faqs: [
    {
      question: 'What year levels do you tutor in Caroline Springs?',
      answer: 'Prep through Year 8. Foundations covers all year levels in English and maths; NAPLAN, selective entry and scholarship programs target specific testing years.',
    },
    {
      question: 'When can my child start?',
      answer: 'The Caroline Springs centre is open and accepting new students. Most families start with a free assessment, then begin classes the following week.',
    },
    {
      question: 'How does NAPLAN preparation work?',
      answer: '10-week term covering reading, writing, language conventions and numeracy. Aligned to ACARA test specifications for Years 3, 5 and 7.',
    },
    {
      question: 'Which selective schools do Caroline Springs students target?',
      answer: 'Suzanne Cory (closest at 16km), Mac.Robertson Girls, Melbourne High and Nossal High. Our Selective Entry program covers all four entry tests, with the Year 8 SEAL test as the entry point.',
    },
    {
      question: 'What does the free assessment include?',
      answer: 'A full assessment, a 1:1 parent consultation, and a written learning plan with recommended program.',
    },
    {
      question: 'Do you offer online tutoring?',
      answer: 'Yes. Online tutoring is available for families who prefer remote sessions or live further from the centre.',
    },
  ],
};
